import React, { useEffect, useRef } from 'react';
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Tutorial = ({ run, setRun }) => {
    const { t, language } = useI18n();
    const { user, setUser } = useAuth();
    const location = useLocation();
    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
    const driverRef = useRef(null);

    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getPageId = () => {
        if (location.pathname === '/dashboard') return 'dashboard';
        if (location.pathname === '/recipes') return 'recipes';
        if (location.pathname === '/recipes/new') return 'recipe_form';
        if (location.pathname === '/menus') return 'menus_list';
        if (location.pathname === '/history') return 'history';
        if (location.pathname.startsWith('/menu/')) return 'menu_editor';
        if (location.pathname.startsWith('/recipes/') && location.pathname.endsWith('/edit')) return 'recipe_form';
        return null;
    };

    const getSteps = () => {
        const pageId = getPageId();

        const dashboardSteps = [
            {
                popover: {
                    title: language === 'es' ? '¡Bienvenido a MealPlanner!' : 'Welcome to MealPlanner!',
                    description: language === 'es'
                        ? 'Te ayudaremos a planificar tus comidas semanales de forma sencilla y saludable.'
                        : 'We will help you plan your weekly meals in a simple and healthy way.',
                    position: 'center'
                }
            },
            {
                element: '[data-testid="today-plan-card"]',
                popover: {
                    title: language === 'es' ? 'Tu Plan de Hoy' : 'Your Today\'s Plan',
                    description: language === 'es'
                        ? 'Aquí verás lo que tienes planeado para hoy de un vistazo.'
                        : 'Here you will see what you have planned for today at a glance.',
                    side: 'bottom',
                    align: 'center'
                }
            },
            {
                element: '[data-testid="day-card-monday"]',
                popover: {
                    title: language === 'es' ? 'Resumen Semanal' : 'Weekly Overview',
                    description: language === 'es'
                        ? 'Este es tu resumen semanal. Puedes ver el menú de cada día aquí.'
                        : 'This is your weekly overview. You can see each day\'s menu here.',
                    side: 'top',
                    align: 'center'
                }
            },
            {
                element: isMobile ? '.tutorial-recipes-mobile' : '.tutorial-recipes-desktop',
                popover: {
                    title: language === 'es' ? 'Tus Recetas' : 'Your Recipes',
                    description: language === 'es'
                        ? 'Desde aquí puedes gestionar tus recetas o importar nuevas desde una URL.'
                        : 'From here you can manage your recipes or import new ones from a URL.',
                    side: 'top',
                    align: 'center'
                }
            },
            {
                element: '[data-testid="new-menu-button"]',
                popover: {
                    title: language === 'es' ? 'Generar Menú' : 'Generate Menu',
                    description: language === 'es'
                        ? '¡Usa este botón para generar un nuevo menú equilibrado para la próxima semana!'
                        : 'Use this button to generate a new balanced menu for next week!',
                    side: 'top',
                    align: 'center'
                }
            },
            {
                element: isMobile ? '.tutorial-settings-mobile' : '.tutorial-settings-desktop',
                popover: {
                    title: language === 'es' ? 'Ajustes' : 'Settings',
                    description: language === 'es'
                        ? 'En ajustes puedes cambiar tus preferencias y volver a ver este tutorial cuando quieras.'
                        : 'In settings you can change your preferences and watch this tutorial again whenever you want.',
                    side: 'top',
                    align: 'center'
                }
            },
        ];

        const recipesSteps = [
            {
                popover: {
                    title: language === 'es' ? 'Gestión de Recetas' : 'Recipe Management',
                    description: language === 'es'
                        ? 'Aquí puedes ver todas tus recetas guardadas.'
                        : 'Here you can see all your saved recipes.',
                    position: 'center'
                }
            },
            {
                element: '[data-testid="new-recipe-button"]',
                popover: {
                    title: language === 'es' ? 'Añadir Recetas' : 'Add Recipes',
                    description: language === 'es'
                        ? 'Añade tus propias recetas manualmente o impórtalas desde una dirección web.'
                        : 'Add your own recipes manually or import them from a web address.',
                    side: 'bottom',
                    align: 'center'
                }
            },
            {
                element: '[data-testid="recipe-search-input"]',
                popover: {
                    title: language === 'es' ? 'Buscar y Filtrar' : 'Search and Filter',
                    description: language === 'es'
                        ? 'Encuentra rápidamente la receta que buscas por nombre o categoría.'
                        : 'Quickly find the recipe you\'re looking for by name or category.',
                    side: 'bottom',
                    align: 'center'
                }
            }
        ];

        const recipeFormSteps = [
            {
                popover: {
                    title: language === 'es' ? 'Crear/Editar Receta' : 'Create/Edit Recipe',
                    description: language === 'es'
                        ? 'Rellena los detalles de tu receta aquí.'
                        : 'Fill in your recipe details here.',
                    position: 'center'
                }
            },
            {
                element: '[data-testid="show-import-dialog-button"]',
                popover: {
                    title: language === 'es' ? 'Importar con IA' : 'Import with AI',
                    description: language === 'es'
                        ? '¡Pega un enlace de cualquier web y la IA rellenará los datos por ti!'
                        : 'Paste a link from any website and AI will fill in the data for you!',
                    side: 'bottom',
                    align: 'center'
                }
            },
            {
                element: '[data-testid="recipe-calories-input"]',
                popover: {
                    title: language === 'es' ? 'Calorías e IA' : 'Calories & AI',
                    description: language === 'es'
                        ? 'Si dejas las calorías vacías, la IA las calculará automáticamente por ti.'
                        : 'If you leave calories empty, AI will calculate them automatically for you.',
                    side: 'top',
                    align: 'center'
                }
            },
            {
                element: '[data-testid="save-recipe-button"]',
                popover: {
                    title: language === 'es' ? 'Guardar' : 'Save',
                    description: language === 'es'
                        ? 'No olvides guardar tus cambios.'
                        : 'Don\'t forget to save your changes.',
                    side: 'top',
                    align: 'center'
                }
            }
        ];

        const menusListSteps = [
            {
                popover: {
                    title: language === 'es' ? 'Tus Menús' : 'Your Menus',
                    description: language === 'es'
                        ? 'Aquí puedes ver tu menú actual y los próximos que hayas generado.'
                        : 'Here you can see your current menu and the upcoming ones you\'ve generated.',
                    position: 'center'
                }
            },
            {
                element: '[data-testid="current-week-menu"]',
                popover: {
                    title: language === 'es' ? 'Semana Actual' : 'Current Week',
                    description: language === 'es'
                        ? 'Este es el menú que estás siguiendo esta semana.'
                        : 'This is the menu you are following this week.',
                    side: 'bottom',
                    align: 'center'
                }
            },
            {
                element: '[data-testid="new-menu-button"]',
                popover: {
                    title: language === 'es' ? 'Nuevo Menú' : 'New Menu',
                    description: language === 'es'
                        ? 'Crea un nuevo menú para la próxima semana desde aquí.'
                        : 'Create a new menu for next week from here.',
                    side: 'bottom',
                    align: 'center'
                }
            }
        ];

        const historySteps = [
            {
                popover: {
                    title: language === 'es' ? 'Historial de Menús' : 'Menu History',
                    description: language === 'es'
                        ? 'Aquí se guardan todos los menús que has generado anteriormente.'
                        : 'All previously generated menus are saved here.',
                    position: 'center'
                }
            },
            {
                element: '[data-testid^="history-menu-"]',
                popover: {
                    title: language === 'es' ? 'Reutilizar Menús' : 'Reuse Menus',
                    description: language === 'es'
                        ? 'Puedes volver a ver un menú antiguo o copiarlo para usarlo como base para uno nuevo.'
                        : 'You can view an old menu or copy it to use as a base for a new one.',
                    side: 'bottom',
                    align: 'center'
                }
            }
        ];

        const menuEditorSteps = [
            {
                popover: {
                    title: language === 'es' ? 'Detalle del Menú' : 'Menu Detail',
                    description: language === 'es'
                        ? 'Personaliza y gestiona tu menú semanal.'
                        : 'Customize and manage your weekly menu.',
                    position: 'center'
                }
            },
            {
                element: '[data-testid="generate-menu-button"], [data-testid="save-menu-button"]',
                popover: {
                    title: language === 'es' ? 'Acciones' : 'Actions',
                    description: language === 'es'
                        ? 'Genera el menú automáticamente con IA o guarda tus cambios manuales.'
                        : 'Generate the menu automatically with AI or save your manual changes.',
                    side: 'bottom',
                    align: 'center'
                }
            },
            {
                element: '[data-testid="shopping-list-button"]',
                popover: {
                    title: language === 'es' ? 'Lista de la Compra' : 'Shopping List',
                    description: language === 'es'
                        ? 'Genera automáticamente una lista de todos los ingredientes que necesitas.'
                        : 'Automatically generate a list of all the ingredients you need.',
                    side: 'bottom',
                    align: 'center'
                }
            },
            {
                element: '[data-testid="download-menu-image-button"]',
                popover: {
                    title: language === 'es' ? 'Descargar Imagen' : 'Download Image',
                    description: language === 'es'
                        ? 'Descarga una imagen bonita de tu menú para compartirla o imprimirla.'
                        : 'Download a beautiful image of your menu to share or print.',
                    side: 'bottom',
                    align: 'center'
                }
            },
            {
                element: '[data-testid^="slot-"]',
                popover: {
                    title: language === 'es' ? 'Personalizar Comidas' : 'Customize Meals',
                    description: language === 'es'
                        ? 'Pulsa en cualquier comida para cambiar la receta por otra que te guste más.'
                        : 'Click on any meal to change the recipe for another one you like better.',
                    side: 'bottom',
                    align: 'center'
                }
            }
        ];

        switch (pageId) {
            case 'dashboard': return dashboardSteps;
            case 'recipes': return recipesSteps;
            case 'recipe_form':
                return recipeFormSteps.filter(step => {
                    if (user?.plan === 'premium') return true;
                    // Filter out AI import and Calorie tracking for free users if mentioned in title or description
                    // or based on data-testid
                    const premiumTestIds = ['[data-testid="show-import-dialog-button"]', '[data-testid="recipe-calories-input"]'];
                    return !premiumTestIds.includes(step.element);
                });
            case 'menus_list': return menusListSteps;
            case 'history': return historySteps;
            case 'menu_editor': return menuEditorSteps;
            default: return [];
        }
    };

    useEffect(() => {
        const steps = getSteps();
        if (run && steps.length > 0 && !driverRef.current) {
            const driverObj = driver({
                showProgress: true,
                steps: steps,
                nextBtnText: language === 'es' ? 'Siguiente' : 'Next',
                prevBtnText: language === 'es' ? 'Anterior' : 'Previous',
                doneBtnText: language === 'es' ? 'Finalizar' : 'Finish',
                progressText: language === 'es' ? '{{current}} de {{total}}' : '{{current}} of {{total}}',
                onDestroyed: () => {
                    setRun(false);
                    driverRef.current = null;
                    markAsSeen();
                },
            });

            driverRef.current = driverObj;
            driverObj.drive();
        }

        return () => {
            if (driverRef.current) {
                driverRef.current.destroy();
                driverRef.current = null;
            }
        };
    }, [run, location.pathname]);

    const markAsSeen = async () => {
        const pageId = getPageId();
        if (!user || !pageId) return;

        try {
            const response = await fetch(`${API}/auth/tutorial-seen?page=${pageId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });

            if (response.ok) {
                const updatedPages = [...(user.tutorial_pages_seen || []), pageId];
                setUser({ ...user, tutorial_pages_seen: updatedPages });
            }
        } catch (error) {
            console.error('Error marking tutorial as seen:', error);
        }
    };

    return null; // driver.js handles its own UI
};

export default Tutorial;
